import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
	ProceedModalComponent,
	ProceedModalData,
} from '@components/proceed-modal/proceed-modal.component';
import { ICON } from '@core/util/icon';
import { LLCFunctions } from '@functions/llc.service';
import { FinancialInstitution } from '@models/lender';
import { LLCTransaction } from '@models/llc-mortgages';
import { Matter, MatterBusinessUser } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { InstitutionRepo } from '@repos/institution/institution.service';
import { LlcRepoService } from '@repos/mortgages/llc.service';
import { LLCMortgageRepoService } from '@repos/mortgages/llcMortgageRepo';
import { NavigationService } from '@services/nav/nav.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { LlcState } from '@state/llc/llc.state';
import { MatterDispatch } from '@state/matter';
import LogRocket from 'logrocket';
import { BehaviorSubject, Observable, combineLatest, from, of } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	map,
	shareReplay,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';
import { LLCLoginComponent } from 'src/app/private/profile/integrations/llc/login/login.component';
import { CloserSyncTransactionRepoService } from '@repos/mortgages/repos/closerSyncTransactionRepo';
import { CloserSyncTransaction } from '@models/mortgages/closer-sync-instruction';
import {
	BaseTransactionAddress,
	MortgageInstructionLender,
	MortgageTransactionSource,
	MortgageTransactionStatus,
} from '@models/mortgages/mortgage-base';
import { getLLCClientName } from '@core/util/llc';
import { isSet } from '@core/util/value';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { dollarsToCents } from '@core/util/money';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { LeapLawyerClerkService } from 'src/app/public/leap/leap-lawyer-clerk-service';
import { SyncMortgagesInstructionsSelectors } from '@state/business/mortgages-instructions/sync-mortgages-instructions/sync-mortgages-instructions.selectors';

interface FilteringOption {
	initials?: boolean;
	label: string; // the value displayed on the pill
	filterFn: (row: TableRow) => void;
}

type LenderData = FinancialInstitution | MortgageInstructionLender;

interface TableRow {
	source: MortgageTransactionSource;
	address: BaseTransactionAddress;
	borrowers: string[];
	closingDate: string;
	fileType: string;
	id: string;
	lawyer: string;
	lender: LenderData;
	mmsFileType?: string;
	principalAmount: number;
	transaction: LLCTransaction | CloserSyncTransaction;
	lenderName: string;
	lenderShortName: string;
	lenderLogoUrl: string;
	lenderShowShortName: boolean;
	lenderShowName: boolean;
}

type AvailableColumns = keyof TableRow;

@UntilDestroy()
@Component({
	selector: 'app-select-mortgage-instructions',
	templateUrl: './select-mortgage-instructions.component.html',
	styleUrls: ['./select-mortgage-instructions.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SelectMortgageInstructionsComponent implements OnInit {
	displayedColumns: AvailableColumns[] = [
		'source',
		'lawyer',
		'borrowers',
		'address',
		'lender',
		'principalAmount',
		'closingDate',
	];

	data$!: Observable<TableRow[]>;

	filters$ = new BehaviorSubject<FilteringOption[]>([]);

	ICON = ICON;

	public lawyersMap$ = this.store.select(BusinessUsersState.lawyersMap);

	public unassignedInstructions$ = this.store.select(
		SyncMortgagesInstructionsSelectors.getUnassignedTransactions,
	);

	@SelectSnapshot(SelectedMatter.getSelected) matter: Matter;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	noMatchingRows$!: Observable<boolean>;

	selectedRow$ = new BehaviorSubject<TableRow>(null);

	selectDisabled$!: Observable<boolean>;

	tryRemovingFilter$!: Observable<boolean>;

	mmsInvalidFileTypeWarning$!: Observable<string>;

	working$ = new BehaviorSubject(false);

	TRANSACTION_SOURCE = MortgageTransactionSource;

	constructor(
		private llcMortgageRepo: LLCMortgageRepoService,
		private institutionRepo: InstitutionRepo,
		private dialog: MatDialog,
		private dialogRef: MatDialogRef<SelectMortgageInstructionsComponent>,
		private matterDispatch: MatterDispatch,
		private llcFunctions: LLCFunctions,
		private snackbar: SnackbarService,
		private navigationService: NavigationService,
		private llcRepoService: LlcRepoService,
		private closerSyncRepo: CloserSyncTransactionRepoService,
		private analytics: AnalyticsService,
		private leapLawyerClerkService: LeapLawyerClerkService,
		private store: Store,
	) {}

	ngOnInit(): void {
		// lets create our core data stream
		const rows$ = combineLatest([
			combineLatest([
				this.llcMortgageRepo.getNewTransactions() as Observable<
					LLCTransaction[]
				>,
				this.llcMortgageRepo.getUnassignedTransactions() as Observable<
					LLCTransaction[]
				>,
				this.unassignedInstructions$,
			]).pipe(
				map(([allNew, unassigned, closerSync]) => [
					...allNew,
					...unassigned,
					...closerSync,
				]),
			),
			this.institutionRepo.All() as Observable<FinancialInstitution[]>,
		]).pipe(
			map(([rows]) =>
				rows.map((row) => {
					const mmsFileType =
						row.overview?.transactionType?.toLowerCase() ?? null;

					const returnRow: TableRow = {
						source: row.source,
						address: row.overview?.propertyAddress,
						closingDate: row.overview?.closingDate,
						fileType: row.overview?.transactionType?.toLowerCase(),
						id: row.id,
						lawyer: row.assignedLawyer,
						lender: row.lender,
						mmsFileType,
						transaction: row,
						principalAmount:
							row.source === MortgageTransactionSource.LLC
								? dollarsToCents(row.overview?.mortgageAmount)
								: row.overview?.mortgageAmount,
						borrowers: row.overview?.mortgagors?.map(getLLCClientName) || [],
						lenderName: row?.overview?.lenderName,
						lenderShortName: row?.overview?.lenderShortName,
						lenderLogoUrl: row?.overview?.logoUrl ?? null,
						lenderShowName:
							!isSet(row?.overview?.logoUrl) &&
							!isSet(row?.overview?.lenderShortName),
						lenderShowShortName: !isSet(row?.overview?.logoUrl),
					};

					return returnRow;
				}),
			),
			shareReplay(1),
		);

		// if a row is selected, and its flagged as a MMS row,
		// we should show the warning when it doesn't match
		// our current matters fileType
		this.mmsInvalidFileTypeWarning$ = this.selectedRow$.pipe(
			map((selected) => {
				if (selected?.mmsFileType) {
					const { fileType } = this.matter;
					return fileType !== selected?.fileType
						? `The selected instructions must be assigned to a ${selected.mmsFileType} file.`
						: '';
				}
				return '';
			}),
		);

		// and now, merge the core stream and our filters
		this.data$ = combineLatest([rows$, this.filters$]).pipe(
			map(([rows, filters]) => this.applyFilters(rows, filters)),
			shareReplay(1),
		);

		// show no matching rows message if no rows
		this.noMatchingRows$ = this.data$.pipe(map((rows) => !!rows.length));

		// show the try removing filters message if the original source
		// has rows, and there are filters applied
		this.tryRemovingFilter$ = combineLatest([rows$, this.filters$]).pipe(
			map(([rows, filters]) => !!(rows.length && filters.length)),
			distinctUntilChanged(),
		);

		// disable the submit if we're doing things
		// or there's nothing selected
		// or, the Mms warning is visible
		this.selectDisabled$ = combineLatest([
			this.selectedRow$,
			this.working$,
			this.mmsInvalidFileTypeWarning$,
		]).pipe(
			map(
				([selectedRow, isWorking, showMmsWarning]) =>
					!selectedRow || isWorking || !!showMmsWarning,
			),
		);

		// check to see if we should add default filters
		if (this.matter?.id) {
			const { lawyerAssigned } = this.matter;
			rows$.pipe(untilDestroyed(this), take(1)).subscribe((rows) => {
				const lawyerFilter = lawyerAssigned
					? this.makeLawyerAssignedFilter(lawyerAssigned)
					: null;
				const lawyerMatches = lawyerFilter
					? !!this.applyFilters(rows, [lawyerFilter])?.length
					: 0;

				if (lawyerMatches) {
					this.addFilter(lawyerFilter);
				}
			});
		}
	}

	// handles adding an item to our filter array
	addFilter(filter: FilteringOption): void {
		const { filters$ } = this;
		const all = filters$.getValue();
		all.push(filter);
		filters$.next(all);
	}

	// convenience function
	makeLawyerAssignedFilter(lawyer: MatterBusinessUser = null): FilteringOption {
		if (lawyer) {
			return {
				initials: true,
				label: lawyer.name,
				filterFn: (row) => row?.lawyer === lawyer.id,
			};
		}
	}

	onConnectLawyerClick(): void {
		this.dialog.open(LLCLoginComponent, LlcState.llcDialogConfig);
	}

	onSelectMortgageClick(): void {
		const { transaction } = this.selectedRow$.getValue();
		const matterId = this.matter?.id;
		const errorData = {
			id: transaction?.id,
			status: transaction?.status,
		};

		if (transaction && matterId) {
			this.analytics.logEvent(AnalyticsEvent.SYNC_MORTGAGE_PAGE_ASSIGN_INFILE);
			const { lawyerToBeReassignedTo } = this;
			let canProceed$!: Observable<boolean>;

			if (lawyerToBeReassignedTo) {
				// if we need to reassign, lets grab the lawyer it needs to get flipped to
				// then ask for permission to update it
				canProceed$ = this.lawyersMap$.pipe(
					take(1),
					tap(() => this.working$.next(true)),
					map((lawyers) => [
						lawyers.get(lawyerToBeReassignedTo),
						lawyers.get(this.matter.lawyerAssigned?.id),
					]),
					switchMap(([shouldBe, assigned]) => {
						// default case - NO lawyer is assigned
						const proceedData: ProceedModalData = {
							noLabel: 'Cancel',
							primaryText: "Lawyer doesn't match", // `This file is currently assigned to ${assigned.firstName} ${assigned.lastName}.`,
							secondaryText: `The file is currently <b>unassigned</b>, but the mortgage instructions are associated with <b>${shouldBe?.firstName} ${shouldBe?.lastName}</b>.<br><br>If you assign these instructions to the file, it will be assigned to <b>${shouldBe?.firstName} ${shouldBe?.lastName}</b>. Do you wish to continue?`,
							yesLabel: 'Assign & continue',
						};

						if (assigned) {
							proceedData.secondaryText = `The file is currently assigned to <b>${assigned.firstName} ${assigned.lastName}</b>, but the mortgage instructions are associated with <b>${shouldBe?.firstName} ${shouldBe?.lastName}</b>.<br><br>If you assign these instructions to the file, it will be reassigned to <b>${shouldBe?.firstName} ${shouldBe?.lastName}</b>. Do you wish to continue?`;
							proceedData.yesLabel = 'Reassign & continue';
						}

						return this.dialog
							.open(ProceedModalComponent, {
								disableClose: true,
								data: proceedData,
								width: '500px',
								maxWidth: '95vw',
							})
							.afterClosed()
							.pipe(
								// if permission was accepted, update the matter before we continue
								tap((proceed) => {
									if (proceed) {
										this.matterDispatch.SetAssignedLawyer(
											matterId,
											shouldBe?.id,
											shouldBe?.firstName,
											shouldBe?.lastName,
										);

										if (this.isLeapMatter) {
											this.leapLawyerClerkService.syncMatterUser(
												matterId,
												shouldBe,
												true,
												true,
											);
										}
									}
									this.working$.next(false);
								}),
							);
					}),
				);
			} else {
				// otherwise, just let it resolve with a truthy
				canProceed$ = of(true);
			}

			// if this requires acceptance, lets ask for that once any previous steps are complete
			if (transaction?.source === MortgageTransactionSource.CLOSER_SYNC) {
				this.acceptCloserSyncMortgage(transaction, canProceed$, matterId);
			} else {
				this.acceptLLCMortgage(
					errorData,
					transaction as LLCTransaction,
					canProceed$,
					matterId,
				);
			}
		}
	}

	acceptCloserSyncMortgage(
		transaction: CloserSyncTransaction,
		canProceed$: Observable<boolean>,
		matterId: string,
	) {
		canProceed$
			.pipe(
				filter((proceed) => !!proceed),
				tap(() => this.working$.next(true)),
			)
			.subscribe(() => {
				this.navigationService.goToMatter_Mortgage_CloserSync_Create(
					matterId,
					transaction.id,
					'existingMatter',
				);
				this.dialogRef.close();
			});
	}

	acceptLLCMortgage(
		errorData: { id: string; status: MortgageTransactionStatus },
		transaction: LLCTransaction,
		canProceed$: Observable<boolean>,
		matterId: string,
	) {
		if (!transaction?.data) {
			canProceed$ = canProceed$.pipe(
				filter((proceed) => !!proceed),
				tap(() => this.working$.next(true)),
				switchMap(() =>
					this.llcFunctions.forceResetTransactionData(transaction.id),
				),
				map(() => true),
				catchError((err) => {
					console.warn(err);
					LogRocket.error('unable to fetch transaction overview data', {
						...errorData,
						error: err,
					});
					this.snackbar.openSnackBar({
						text: 'Unable to accept LLC Instruction',
						icon: ICON.WARNING,
					});
					return of(false);
				}),
			);
		}

		if (transaction.status === MortgageTransactionStatus.PENDING_ACCEPTANCE) {
			canProceed$ = canProceed$.pipe(
				filter((proceed) => !!proceed), // ensure we are still a go
				tap(() => this.working$.next(true)), // notify that we're still doing things
				switchMap(() => this.llcFunctions.acceptDeal(transaction.id)), // accept it
				map(() => true), // the above has an incorrect response payload.  error = fail, 200/ok = pass
				catchError((err) => {
					console.warn(err);
					LogRocket.error('unable to accept LLC Instruction', {
						...errorData,
						error: err,
					});
					this.snackbar.openSnackBar({
						text: 'Unable to accept LLC Instruction',
						icon: ICON.WARNING,
					});
					return of(false);
				}),
			);
		}

		// and finally, take the above, and attempt to assign it to the matter, then redirect
		// to let them continue the flow
		canProceed$
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((proceed) => !!proceed),
				switchMap(() =>
					from(
						this.llcRepoService.assignMatterToMortgage(
							transaction.id,
							matterId,
						),
					),
				),
			)
			.subscribe(
				({ matterMortgageId }) => {
					this.navigationService.goToMatter_Mortgages_Mortgage_AssignSync(
						matterId,
						matterMortgageId,
					);
					this.dialogRef.close();
				},
				(err) => {
					console.warn('An unexpected error occured', err);
					LogRocket.error('An unexpected LLC error occured', {
						...errorData,
						err,
					});
					this.snackbar.openSnackBar({
						text: 'Unable to accept LLC Instruction',
						icon: ICON.WARNING,
					});
				},
				() => {
					this.working$.next(false);
				},
			);
	}

	// removes an option from our filter array
	removeFilter(filter: FilteringOption): void {
		const { filters$ } = this;
		const all = filters$.getValue();
		const idx = all.indexOf(filter);
		if (idx > -1) {
			all.splice(idx, 1);
			filters$.next(all);
		}
	}

	// returns the string of the lawyer in the selected row
	// if it doesn't match the value on the matter
	private get lawyerToBeReassignedTo(): string {
		const selected = this.selectedRow$.getValue();
		const matter = this.matter;

		if (selected && matter && selected.lawyer !== matter.lawyerAssigned?.id) {
			return selected.lawyer;
		}

		return '';
	}

	private applyFilters(
		rows: TableRow[],
		filters: FilteringOption[],
	): TableRow[] {
		if (filters?.length && rows?.length) {
			return rows?.filter((row) =>
				filters.every(({ filterFn }) => filterFn(row)),
			);
		}

		return rows || [];
	}
}
